import { continueWatchingActiveSelector, continueWatchingItemsSelector } from 'app/reducers/continueWatching';
import { accessVersionChoiceSelector } from 'app/reducers/videoPage';
import { withRouter } from 'next/router';
import React from 'react';
import { connect } from 'react-redux';
import { updateAccessVersionChoice } from '../../actions/videoPage';
import env from '../../env';
import { fontWeightBold, regularWhite } from '../../ui-components/design-tokens';
import PlayImage from '../../ui-components/PlayImage/PlayImage';
import { Heading1 } from '../../ui-components/typography/Heading.styles';
import { TYPES } from '../../ui-components/typography/PlayText/PlayText';
import { browserMeasurements } from '../../utils/browserMeasurements';
import { createTitleStatistics, createVideoStatistics } from '../../utils/contentStatistics';
import { errorStatistics } from '../../utils/statistics/universalStatistics';
import { contentoImageUrl, DEFAULT_IMAGE_LARGE } from '../../utils/svtimage';
import { ReactHelmetPlay } from '../ApplicationHandler/ReactHelmet';
import SendStatistics from '../CommonElements/SendStatistics';
import ContentoQuery, { ContentoData, ContentoError, ContentoLoading } from '../Contento/ContentoQuery';
import { NotFoundContainer } from '../ErrorHandler';
import ErrorContainer from '../ErrorHandler/ErrorContainer';
import SimilarVideoContentContainer from '../SimilarVideoContent/SimilarVideoContentContainer';
import OpenModal from '../VideoModal/OpenModal';
import AssociatedContent from './components/AssociatedContent/AssociatedContent';
import CastAndCrew from './components/CastAndCrew/CastAndCrew';
import Categories from './components/Categories/Categories';
import EditorialMessage from './components/EditorialMessage/EditorialMessage';
import Highlights from './components/Highlights/Highlights';
import Metadata from './components/Metadata/Metadata';
import ShowMore from './components/ShowMore/ShowMore';
import SMART_START_QUERY from './SmartStartQuery.graphql';
import {
    Byline,
    HiddenByline,
    StyledBlurHashCanvas,
    StyledTopArea,
    StyledTopAreaBadge,
    StyledTopAreaLeft,
    StyledTopAreaLeftPlaceholder,
    StyledTopAreaMeta,
    StyledTopAreaPlaceholder,
    StyledTopAreaPlayButton,
    StyledTopAreaPlayButtonText,
    StyledTopAreaRight,
    StyledTopAreaRightPlaceholder,
    StyledTopAreaSubheading,
    NotAvailableInSweden,
} from './components/TopArea/TopArea.styles';
import TopAreaVariants from './components/TopArea/TopAreaVariants';
import { getAssociatedContentProgress, getSmartStartProgress, getSubheading } from './DetailsPage.helpers';
import {
    ContentWarning,
    Description,
    DetailArea,
    MoreDetails,
    StyledDetailsPage,
    StyledProgressBar,
} from './DetailsPage.styles';
import DETAILS_PAGE_QUERY from './DetailsPageQuery.graphql';
import { mapToNewAccessService } from 'app/domain/AccessServiceTypes';

const getStatisticsTitle = (detailsPage, idOrSlug) => {
    let statisticsObject;
    if (typeof window !== 'undefined') {
        const browserSize = browserMeasurements(window);

        statisticsObject = createTitleStatistics(
            idOrSlug,
            detailsPage.item.svtId,
            detailsPage.analyticsIdentifiers.viewId,
            detailsPage.analyticsIdentifiers.viewType,
            browserSize
        );
    }

    return statisticsObject;
};

const getStatisticsVideo = (detailsPage, router) => {
    let statisticsObject;
    if (typeof window !== 'undefined') {
        const browserSize = browserMeasurements(window);

        statisticsObject = createVideoStatistics(
            router.query.titleSlug,
            router.query.episodeSlug,
            detailsPage.item.svtId,
            detailsPage.analyticsIdentifiers.viewId,
            detailsPage.analyticsIdentifiers.viewType,
            browserSize
        );
    }

    return statisticsObject;
};

class DetailsPage extends React.Component {
    shouldComponentUpdate(nextProps) {
        return nextProps.openModal === false;
    }

    render() {
        const {
            id: idOrSlug,
            isTitle,
            router,
            updateAccessibilityVersion,
            continueWatchingIsActive,
            continueWatching,
            currentAccessibilityChoice,
        } = this.props;

        return (
            <ContentoQuery
                query={DETAILS_PAGE_QUERY}
                fetchPolicy="cache-and-network"
                variables={{
                    id: idOrSlug,
                    includeFullOppetArkiv: env.FLAG_ENABLE_ALL_OA,
                }}
            >
                <ContentoLoading>
                    <StyledDetailsPage>
                        <StyledTopAreaPlaceholder>
                            <StyledTopAreaLeftPlaceholder />
                            <StyledTopAreaRightPlaceholder />
                        </StyledTopAreaPlaceholder>
                    </StyledDetailsPage>
                </ContentoLoading>
                <ContentoData>
                    {(contentoData) => {
                        const detailsPage = contentoData?.detailsPage;

                        if (!detailsPage) {
                            return (
                                <>
                                    <NotFoundContainer shouldDispatchHttpStatus={true} baseContainer />
                                    <SendStatistics statisticsObject={errorStatistics(404, router.asPath)} />
                                </>
                            );
                        }

                        const titleArticleId = detailsPage.item?.parent?.id;

                        const topImage = contentoImageUrl(
                            detailsPage.images.wide,
                            'large',
                            1024,
                            DEFAULT_IMAGE_LARGE
                        );

                        const isSingle = detailsPage?.item?.parent?.__typename === 'Single';
                        const canonical = env.BASE_URL + detailsPage?.item?.urls?.svtplay;
                        const similarContent = detailsPage.similarContent;
                        const metaName =
                            isTitle || isSingle || !detailsPage.item.parent
                                ? detailsPage.item.name
                                : `${detailsPage.item.parent.name} – ${detailsPage.item.name}`;

                        let history = [];

                        if (continueWatchingIsActive && continueWatching) {
                            history = continueWatching
                                .filter((item) => {
                                    return item.get('titleArticleId') === titleArticleId;
                                })
                                .toJS()
                                .map((continueWatchingItem) => ({
                                    id: continueWatchingItem.videoId,
                                    progressSeconds: parseInt(continueWatchingItem.position, 10) || 0,
                                }));
                        }

                        return (
                            <StyledDetailsPage itemScope={true} itemType="http://schema.org/VideoObject">
                                <meta itemProp="description" content={detailsPage.description} />
                                <meta itemProp="image" content={topImage} />
                                <meta itemProp="name" content={detailsPage.item.name} />

                                {!isTitle && (
                                    <>
                                        <meta
                                            itemProp="duration"
                                            content={detailsPage.item.durationISOFormatted}
                                        />
                                        <meta itemProp="uploadDate" content={detailsPage.item.validFrom} />
                                        <meta itemProp="expires" content={detailsPage.item.validTo} />
                                    </>
                                )}
                                <ReactHelmetPlay
                                    pathForFeeds={router.asPath}
                                    title={metaName}
                                    description={detailsPage.description}
                                    image={topImage}
                                >
                                    <meta
                                        property="og:type"
                                        key="og:type"
                                        content={isTitle ? 'video.tv_show' : 'video.episode'}
                                    />
                                    <meta property="og:image" key="og:image" content={topImage} />
                                    <meta
                                        name="twitter:image:src"
                                        key="twitter:image:src"
                                        content={topImage}
                                    />
                                    <meta property="og:url" key="og:url" content={canonical} />
                                    <meta name="twitter:url" key="twitter:url" content={canonical} />
                                    <link rel="canonical" key="canonical" href={canonical} />
                                </ReactHelmetPlay>
                                <ContentoQuery
                                    query={SMART_START_QUERY}
                                    variables={{
                                        id: idOrSlug,
                                        history,
                                        includeFullOppetArkiv: env.FLAG_ENABLE_ALL_OA,
                                        accessibility: mapToNewAccessService(currentAccessibilityChoice),
                                    }}
                                    fetchPolicy="cache-and-network"
                                    preferGetMethod={false}
                                >
                                    <ContentoData>
                                        {(data) => {
                                            const smartStart = data.smartStart;

                                            const url = smartStart?.item.urls?.svtplay;

                                            const blurHash = detailsPage.images?.wide?.blurHash;

                                            const urgentMessage = detailsPage.item?.messages?.urgent;

                                            const editorialMessage = detailsPage.item?.messages?.editorial;

                                            const highlights = detailsPage.item?.highlights;

                                            const categories = detailsPage.categories;

                                            const badge = smartStart?.badge;

                                            const associatedContent = detailsPage.associatedContent;

                                            const selectedVideoId = smartStart?.videoSvtId;

                                            const variants = smartStart?.item.videos.filter(
                                                (video) => video.svtId && video.svtId !== selectedVideoId
                                            );

                                            const smartStartProgress = getSmartStartProgress(
                                                continueWatchingIsActive,
                                                continueWatching?.toJS(),
                                                smartStart
                                            );

                                            const associatedContentProgress = getAssociatedContentProgress(
                                                continueWatchingIsActive,
                                                continueWatching?.toJS(),
                                                associatedContent
                                            );

                                            const accessibleLabel = isTitle
                                                ? `${smartStart?.label} av ${detailsPage?.item?.parent?.name}`
                                                : `${smartStart?.label} ${
                                                      detailsPage.item?.parent?.name
                                                          ? detailsPage.item.parent.name
                                                          : ''
                                                  } `;

                                            return (
                                                <>
                                                    <StyledTopArea>
                                                        {blurHash && (
                                                            <StyledBlurHashCanvas
                                                                hash={blurHash}
                                                                width={256}
                                                                height={144}
                                                                punch={1}
                                                            />
                                                        )}
                                                        <StyledTopAreaLeft>
                                                            <PlayImage src={topImage} />
                                                            {smartStartProgress ? (
                                                                <StyledProgressBar
                                                                    progress={smartStartProgress}
                                                                    selected
                                                                />
                                                            ) : null}
                                                        </StyledTopAreaLeft>
                                                        <StyledTopAreaRight>
                                                            <StyledTopAreaMeta>
                                                                {badge && (
                                                                    <StyledTopAreaBadge
                                                                        text={badge.text}
                                                                        theme={badge.type}
                                                                        accessibilityText={badge.altText}
                                                                        overlay={false}
                                                                    />
                                                                )}
                                                                {isSingle && (
                                                                    <Byline
                                                                        type={TYPES.SMALL}
                                                                        color={regularWhite}
                                                                        aria-hidden="true"
                                                                    >
                                                                        {detailsPage.item.shortDescription}
                                                                    </Byline>
                                                                )}
                                                                <Heading1>
                                                                    {detailsPage.item?.parent?.name
                                                                        ? detailsPage.item.parent.name
                                                                        : detailsPage.item.name}
                                                                </Heading1>
                                                                <StyledTopAreaSubheading data-rt="video-player-splash-play">
                                                                    {getSubheading(detailsPage.item, isTitle)}
                                                                </StyledTopAreaSubheading>
                                                                {isSingle && (
                                                                    <HiddenByline>
                                                                        {detailsPage.item.shortDescription}
                                                                    </HiddenByline>
                                                                )}
                                                                {url && (
                                                                    <>
                                                                        <OpenModal
                                                                            videoId={selectedVideoId}
                                                                            videoUrl={url}
                                                                        >
                                                                            <StyledTopAreaPlayButton
                                                                                role="button"
                                                                                data-rt="top-area-play-button"
                                                                                onlyAvailableInSweden={
                                                                                    detailsPage.moreDetails
                                                                                        .restrictions
                                                                                        .onlyAvailableInSweden
                                                                                }
                                                                            >
                                                                                <StyledTopAreaPlayButtonText
                                                                                    aria-label={
                                                                                        accessibleLabel
                                                                                    }
                                                                                >
                                                                                    {smartStart?.label}
                                                                                </StyledTopAreaPlayButtonText>
                                                                            </StyledTopAreaPlayButton>
                                                                        </OpenModal>
                                                                        <NotAvailableInSweden
                                                                            weight={fontWeightBold}
                                                                            onlyAvailableInSweden={
                                                                                detailsPage.moreDetails
                                                                                    .restrictions
                                                                                    .onlyAvailableInSweden
                                                                            }
                                                                        >
                                                                            Programmet kan bara ses i Sverige
                                                                        </NotAvailableInSweden>
                                                                    </>
                                                                )}
                                                                <TopAreaVariants
                                                                    variants={variants}
                                                                    onClick={updateAccessibilityVersion}
                                                                />
                                                            </StyledTopAreaMeta>
                                                        </StyledTopAreaRight>
                                                    </StyledTopArea>
                                                    <MoreDetails>
                                                        <DetailArea position="left">
                                                            {urgentMessage && (
                                                                <EditorialMessage
                                                                    {...urgentMessage}
                                                                    urgent
                                                                    boldText
                                                                />
                                                            )}
                                                            <Description type={TYPES.LARGE}>
                                                                {detailsPage.description}
                                                            </Description>
                                                            {smartStart?.contentWarning && (
                                                                <ContentWarning
                                                                    type={TYPES.LARGE}
                                                                    weight={fontWeightBold}
                                                                >
                                                                    {smartStart.contentWarning.text}
                                                                </ContentWarning>
                                                            )}
                                                            {editorialMessage && (
                                                                <EditorialMessage {...editorialMessage} />
                                                            )}
                                                            {highlights && (
                                                                <Highlights
                                                                    videoId={selectedVideoId}
                                                                    videoUrl={url}
                                                                    highlights={highlights}
                                                                />
                                                            )}
                                                            {categories && (
                                                                <Categories categories={categories} />
                                                            )}
                                                        </DetailArea>
                                                        <DetailArea position="right">
                                                            <Metadata
                                                                validFrom={detailsPage.moreDetails?.validFrom}
                                                                validTo={detailsPage.moreDetails?.validTo}
                                                                productionYear={
                                                                    detailsPage?.moreDetails?.productionYear
                                                                }
                                                                originalProgramTitle={
                                                                    detailsPage?.item?.originalProgramTitle
                                                                }
                                                                isUpcomingVOD={detailsPage.isUpcomingVOD}
                                                                onlyAvailableInSwedenFormatted={
                                                                    detailsPage.moreDetails?.restrictions
                                                                        ?.onlyAvailableInSwedenFormatted
                                                                }
                                                                nextEpisodeAvailableFormatted={
                                                                    detailsPage?.item
                                                                        .nextEpisodeAvailableFormatted
                                                                }
                                                            />
                                                        </DetailArea>
                                                        {!isTitle &&
                                                            !isSingle &&
                                                            detailsPage.castAndCrew?.length > 0 && (
                                                                <DetailArea position="left">
                                                                    <ShowMore
                                                                        allByDefault={isTitle || isSingle}
                                                                    >
                                                                        <CastAndCrew
                                                                            castAndCrew={
                                                                                detailsPage.castAndCrew
                                                                            }
                                                                        />
                                                                    </ShowMore>
                                                                </DetailArea>
                                                            )}
                                                    </MoreDetails>

                                                    {!isSingle && (
                                                        <AssociatedContent
                                                            sections={associatedContent}
                                                            selectedVideoId={selectedVideoId}
                                                            progress={associatedContentProgress}
                                                        />
                                                    )}
                                                </>
                                            );
                                        }}
                                    </ContentoData>
                                    <ContentoLoading>
                                        <StyledTopAreaPlaceholder>
                                            <StyledTopAreaLeftPlaceholder />
                                            <StyledTopAreaRightPlaceholder />
                                        </StyledTopAreaPlaceholder>
                                    </ContentoLoading>
                                </ContentoQuery>
                                {similarContent && similarContent.length && (
                                    <SimilarVideoContentContainer
                                        contentList={similarContent[0]}
                                        titleArticleId={titleArticleId}
                                    />
                                )}
                                {isTitle ? (
                                    <SendStatistics
                                        statisticsObject={getStatisticsTitle(detailsPage, idOrSlug)}
                                    />
                                ) : (
                                    <SendStatistics
                                        statisticsObject={getStatisticsVideo(detailsPage, router)}
                                    />
                                )}
                            </StyledDetailsPage>
                        );
                    }}
                </ContentoData>
                <ContentoError>
                    <>
                        <ErrorContainer pathname={router.asPath} shouldDispatchHttpStatus={true} />
                        <SendStatistics statisticsObject={errorStatistics(500, router.asPath)} />
                    </>
                </ContentoError>
            </ContentoQuery>
        );
    }
}

const mapDispatchToProps = (dispatch) => {
    return {
        updateAccessibilityVersion: (variant) => dispatch(updateAccessVersionChoice(variant)),
    };
};

const mapStateToProps = (state, ownProps) => ({
    ...ownProps,
    continueWatchingIsActive: continueWatchingActiveSelector(state),
    continueWatching: continueWatchingItemsSelector(state),
    currentAccessibilityChoice: accessVersionChoiceSelector(state),
});

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(DetailsPage));
